import { useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Recommendation = {
  title: string;
  area: string;
  priority: string;
  why: string;
  first_step: string;
};

interface AiSeoRecommendationsProps {
  language: "es" | "en";
  sourcePage: string;
}

const COPY = {
  es: {
    intro:
      "Describe tu sitio y tu negocio y recibirás una lista priorizada de acciones SEO generada con IA. Son orientaciones preliminares, no una auditoría: no se garantizan posiciones, tráfico ni resultados.",
    name: "Nombre",
    email: "Correo electrónico",
    website: "Sitio web (opcional)",
    description: "Describe tu sitio, tu negocio y qué te preocupa",
    descriptionHint: "Mínimo 40 caracteres. Cuanto más contexto, más útil la lista.",
    submit: "Generar recomendaciones",
    loading: "Generando recomendaciones…",
    consent:
      "Al enviar, tus datos se guardan como contacto para poder responderte. No se envían correos automáticos de marketing.",
    resultTitle: "Acciones priorizadas",
    priority: "Prioridad",
    firstStep: "Primer paso",
    disclaimer:
      "Generado con IA a partir de tu descripción. No sustituye una auditoría técnica ni garantiza resultados.",
    again: "Describir otro sitio",
    errors: {
      invalid_input: "Revisa el nombre, el correo y una descripción de al menos 40 caracteres.",
      lead_not_saved: "No pudimos guardar tus datos. Inténtalo de nuevo en unos minutos.",
      rate_limited: "Demasiadas solicitudes en este momento. Inténtalo en unos minutos.",
      credits_exhausted: "El servicio de IA no está disponible ahora mismo. Escríbeme y lo reviso contigo.",
      generic: "No se pudo generar la lista. Tus datos quedaron guardados; puedo responderte directamente.",
    },
    areas: {
      tecnico: "Técnico",
      contenido: "Contenido",
      autoridad: "Autoridad",
      conversion: "Conversión",
      medicion: "Medición",
    } as Record<string, string>,
    priorities: { alta: "Alta", media: "Media", baja: "Baja" } as Record<string, string>,
  },
  en: {
    intro:
      "Describe your site and your business to get an AI-generated prioritized list of SEO actions. These are preliminary directions, not an audit: no rankings, traffic or results are guaranteed.",
    name: "Name",
    email: "Email",
    website: "Website (optional)",
    description: "Describe your site, your business and what concerns you",
    descriptionHint: "At least 40 characters. More context means a more useful list.",
    submit: "Generate recommendations",
    loading: "Generating recommendations…",
    consent:
      "By submitting, your details are stored as a contact so I can reply. No automated marketing emails are sent.",
    resultTitle: "Prioritized actions",
    priority: "Priority",
    firstStep: "First step",
    disclaimer:
      "AI-generated from your description. It does not replace a technical audit and guarantees no results.",
    again: "Describe another site",
    errors: {
      invalid_input: "Check your name, email and a description of at least 40 characters.",
      lead_not_saved: "We couldn't save your details. Please try again in a few minutes.",
      rate_limited: "Too many requests right now. Please try again in a few minutes.",
      credits_exhausted: "The AI service is unavailable right now. Write to me and I'll review it with you.",
      generic: "The list couldn't be generated. Your details were saved; I can reply directly.",
    },
    areas: {
      tecnico: "Technical",
      contenido: "Content",
      autoridad: "Authority",
      conversion: "Conversion",
      medicion: "Measurement",
    } as Record<string, string>,
    priorities: { alta: "High", media: "Medium", baja: "Low" } as Record<string, string>,
  },
};

const inputClass =
  "mt-2 w-full border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-brand focus:outline-none";
const labelClass = "font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500";

export const AiSeoRecommendations = ({ language, sourcePage }: AiSeoRecommendationsProps) => {
  const t = COPY[language];
  const [form, setForm] = useState({ name: "", email: "", website: "", description: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ summary: string; recommendations: Recommendation[] } | null>(null);

  const update = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.name.trim() || !form.email.trim() || form.description.trim().length < 40) {
      setError(t.errors.invalid_input);
      return;
    }

    setLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("ai-seo-recommendations", {
        body: { ...form, language, source_page: sourcePage },
      });

      const payload = data as
        | { success?: boolean; error?: string; summary?: string; recommendations?: Recommendation[] }
        | null;

      if (payload?.success && Array.isArray(payload.recommendations)) {
        setResult({ summary: payload.summary ?? "", recommendations: payload.recommendations });
      } else {
        const code = payload?.error ?? "generic";
        setError(t.errors[code as keyof typeof t.errors] ?? t.errors.generic);
        if (fnError) console.error(fnError);
      }
    } catch (err) {
      console.error(err);
      setError(t.errors.generic);
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="border border-slate-800 bg-slate-900/40 p-6 lg:p-8">
        <p className={labelClass}>{t.resultTitle}</p>
        {result.summary && (
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-300">{result.summary}</p>
        )}
        <ol className="mt-8 border-t border-slate-800">
          {result.recommendations.map((rec, index) => (
            <li key={`${rec.title}-${index}`} className="border-b border-slate-800 py-5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  {t.areas[rec.area] ?? rec.area} · {t.priority}: {t.priorities[rec.priority] ?? rec.priority}
                </span>
              </div>
              <h3 className="font-display mt-2 text-lg font-bold text-white">{rec.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{rec.why}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">
                <span className="font-semibold">{t.firstStep}:</span> {rec.first_step}
              </p>
            </li>
          ))}
        </ol>
        <p className="mt-6 text-xs leading-relaxed text-slate-500">{t.disclaimer}</p>
        <button
          type="button"
          onClick={() => setResult(null)}
          className="mt-6 border border-slate-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-brand"
        >
          {t.again}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-slate-800 bg-slate-900/40 p-6 lg:p-8">
      <p className="max-w-3xl text-sm leading-relaxed text-slate-400">{t.intro}</p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="ai-seo-name">
            {t.name}
          </label>
          <input id="ai-seo-name" className={inputClass} value={form.name} onChange={update("name")} required />
        </div>
        <div>
          <label className={labelClass} htmlFor="ai-seo-email">
            {t.email}
          </label>
          <input
            id="ai-seo-email"
            type="email"
            className={inputClass}
            value={form.email}
            onChange={update("email")}
            required
          />
        </div>
      </div>

      <div className="mt-6">
        <label className={labelClass} htmlFor="ai-seo-website">
          {t.website}
        </label>
        <input
          id="ai-seo-website"
          className={inputClass}
          placeholder="https://"
          value={form.website}
          onChange={update("website")}
        />
      </div>

      <div className="mt-6">
        <label className={labelClass} htmlFor="ai-seo-description">
          {t.description}
        </label>
        <textarea
          id="ai-seo-description"
          className={`${inputClass} min-h-[140px] resize-y`}
          value={form.description}
          onChange={update("description")}
          required
        />
        <p className="mt-2 text-xs text-slate-500">{t.descriptionHint}</p>
      </div>

      {error && (
        <p role="alert" className="mt-6 border border-brand/50 bg-brand/10 px-4 py-3 text-sm text-white">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="mt-8 inline-flex items-center gap-3 bg-brand px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand/90 disabled:opacity-60"
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {loading ? t.loading : t.submit}
      </button>
      <p className="mt-4 text-xs leading-relaxed text-slate-500">{t.consent}</p>
    </form>
  );
};
